import { useSettingsStore } from '@/stores/settings'
import { isProduction } from '@/types/settings'

/**
 * Get the landing page base URL based on current hostname.
 * Handles the subdomain pattern (app.example.com -> example.com).
 */
export function getLandingBaseUrl(): string {
  if (typeof window === 'undefined') return '/'

  const hostname = window.location.hostname
  const protocol = window.location.protocol

  // Subdomain layout: app.example.com serves the app, example.com the landing.
  if (hostname.startsWith('app.')) {
    return protocol + '//' + hostname.slice('app.'.length)
  }

  // Flat layout: myapp-app.example.com serves the app, myapp.example.com the
  // landing. This is what bin/provision-dns enforces and what the setup wizard
  // derives, so it is the layout a generated service actually gets. Without it
  // the app fell through to '/', which resolves against the app's own host and
  // sends anyone clicking the logo to the sign-in page instead of the landing.
  const flat = /^(.+)-app\.(.+)$/.exec(hostname)
  if (flat !== null) {
    return protocol + '//' + flat[1] + '.' + flat[2]
  }

  return '/'
}

/**
 * Build a landing page URL with optional theme/locale settings as query params.
 *
 * In production: Returns URL without params (cookies with domain=.example.com handle sync)
 * In development: Adds theme/locale params (needed for localhost subdomain sharing)
 *
 * @param path - Optional path on the landing page (default: '/')
 * @returns Full URL, with query parameters only in development
 */
export function getLandingUrlWithSettings(path = '/'): string {
  const baseUrl = getLandingBaseUrl()

  // In production, cookies handle cross-subdomain persistence
  // No need for URL parameters - cleaner UX
  if (isProduction()) {
    if (baseUrl === '/') {
      return path
    }
    try {
      return new URL(path, baseUrl).toString()
    } catch {
      return baseUrl + path
    }
  }

  // In development (localhost), use URL params for cross-subdomain sharing
  // This is needed because cookies don't share well between localhost subdomains
  const settingsStore = useSettingsStore()

  // For relative URLs (when not on subdomain), build URL differently
  if (baseUrl === '/') {
    const params = new URLSearchParams()
    params.set('theme', settingsStore.theme)
    params.set('locale', settingsStore.locale)
    return `${path}?${params.toString()}`
  }

  try {
    const url = new URL(path, baseUrl)
    url.searchParams.set('theme', settingsStore.theme)
    url.searchParams.set('locale', settingsStore.locale)
    return url.toString()
  } catch {
    // Fallback if URL construction fails
    const params = new URLSearchParams()
    params.set('theme', settingsStore.theme)
    params.set('locale', settingsStore.locale)
    return `${baseUrl}${path}?${params.toString()}`
  }
}
